import { useCallback, useEffect, useRef, useState } from 'react';

type Updater<T> = (prev: T) => T;

interface SyncedStorageOptions {
  type?: 'local' | 'session';
  channel?: string;
  lockKey?: string;
}

const withLock = async <T>(
  lockKey: string | undefined,
  fn: () => T | Promise<T>,
) => {
  if (
    !lockKey ||
    typeof navigator === 'undefined' ||
    !('locks' in navigator)
  )
    return fn();

  return navigator.locks.request(lockKey, { mode: 'exclusive' }, fn);
};
const useSyncedStorageState = <T>(
  storageKey: string,
  initialState: T,
  options: SyncedStorageOptions = {},
) => {
  const {
    type = 'session',
    channel = `channel_${storageKey}`,
    lockKey = `lock_${storageKey}`,
  } = options;

  const storage =
    typeof window !== 'undefined'
      ? type === 'local'
        ? localStorage
        : sessionStorage
      : null;

  const readFromStorage = useCallback((): T => {
    if (!storage) return initialState;

    try {
      const item = storage.getItem(storageKey);
      return item ? JSON.parse(item) : initialState;
    } catch {
      return initialState;
    }
  }, [initialState, storage, storageKey]);

  const [state, setState] = useState<T>(readFromStorage);
  const [hydrated, setHydrated] = useState(false);

  const channelRef = useRef<BroadcastChannel | null>(null);

  // Initial sync
  useEffect(() => {
    if (typeof window === 'undefined') return;
    setState(readFromStorage());
    setHydrated(true);
  }, [readFromStorage]);

  // Setup broadcast channel
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const bc = new BroadcastChannel(channel);
    channelRef.current = bc;

    bc.onmessage = (e) => {
      if (e.data && e.data.key === storageKey) {
        setState(e.data.state as T);
      }
    };

    const handleStorageEvent = (e: StorageEvent) => {
      if (e.key === storageKey && e.newValue) {
        setState(JSON.parse(e.newValue));
      }
    };

    if (type === 'local') {
      window.addEventListener('storage', handleStorageEvent);
    }

    return () => {
      bc.close();
      window.removeEventListener('storage', handleStorageEvent);
    };
  }, [channel, storageKey, type]);

  const update = useCallback(
    async (updater: Updater<T>) => {
      await withLock(lockKey, async () => {
        if (!storage) return;

        const currentRaw = storage.getItem(storageKey);
        let currentData: T;

        try {
          currentData =
            currentRaw !== null
              ? JSON.parse(currentRaw)
              : initialState;
        } catch {
          currentData = initialState;
        }

        const nextData =
          typeof updater === 'function'
            ? (updater as (prev: T) => T)(currentData)
            : updater;

        storage.setItem(storageKey, JSON.stringify(nextData));

        // Update local state
        setState(nextData);

        channelRef.current?.postMessage({
          key: storageKey,
          state: nextData,
        });
      });
    },
    [initialState, lockKey, storage, storageKey],
  );

  return { state, update, hydrated };
};

export default useSyncedStorageState;
